/**
 * @name Glass Styles
 * @description Estilos para efeito glassmorphism (acrílico molhado) integrado ao tema
 * @author Keven Adan Bezerra | kevenadanb
 * @package src/themes/styles/glassStyles.ts
 */

import { Theme } from "@mui/material/styles";

/**
 * Aplica efeito glassmorphism com base no tema atual
 */
export const getGlassEffect = (
  theme: Theme,
  intensity: "light" | "medium" | "strong" = "medium",
) => {
  const isDark = theme.palette.mode === "dark";

  // Configurações por intensidade
  const intensityConfig = {
    light: {
      blur: 8,
      opacity: isDark ? 0.15 : 0.25,
      borderOpacity: isDark ? 0.15 : 0.2,
      shadowOpacity: isDark ? 0.3 : 0.15,
    },
    medium: {
      blur: 12,
      opacity: isDark ? 0.25 : 0.35,
      borderOpacity: isDark ? 0.2 : 0.25,
      shadowOpacity: isDark ? 0.4 : 0.2,
    },
    strong: {
      blur: 16,
      opacity: isDark ? 0.35 : 0.45,
      borderOpacity: isDark ? 0.25 : 0.3,
      shadowOpacity: isDark ? 0.5 : 0.25,
    },
  };

  const config = intensityConfig[intensity];

  // Cores base do tema
  const backgroundBase = isDark ? "255, 255, 255" : "255, 255, 255";
  const borderBase = isDark ? "255, 255, 255" : "255, 255, 255";
  const shadowColor = isDark ? "0, 0, 0" : "0, 0, 0";

  return {
    backdropFilter: `blur(${config.blur}px) saturate(1.2)`,
    WebkitBackdropFilter: `blur(${config.blur}px) saturate(1.2)`,
    backgroundColor: `rgba(${backgroundBase}, ${config.opacity})`,
    border: `1px solid rgba(${borderBase}, ${config.borderOpacity})`,
    boxShadow: `
               0 8px 32px rgba(${shadowColor}, ${config.shadowOpacity}),
               inset 0 1px 0 rgba(${borderBase}, ${config.borderOpacity * 1.5})
          `,
    position: "relative" as const,
    overflow: "hidden" as const,
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "1px",
      background: `linear-gradient(90deg, transparent, rgba(${borderBase}, ${config.borderOpacity * 2}), transparent)`,
      zIndex: 1,
    },
  };
};

/**
 * Efeito glassmorphism para Cards
 */
export const getGlassCard = (
  theme: Theme,
  intensity: "light" | "medium" | "strong" = "medium",
) => ({
  ...getGlassEffect(theme, intensity),
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: `
               0 12px 40px rgba(0, 0, 0, ${theme.palette.mode === "dark" ? 0.4 : 0.25}),
               inset 0 1px 0 rgba(255, 255, 255, ${theme.palette.mode === "dark" ? 0.3 : 0.4})
          `,
  },
});

/**
 * Efeito glassmorphism para Modais
 */
export const getGlassModal = (theme: Theme) => ({
  ...getGlassEffect(theme, "strong"),
  margin: "auto",
  maxWidth: "90vw",
  maxHeight: "90vh",
  outline: "none",
  animation: "glassModalAppear 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  "@keyframes glassModalAppear": {
    "0%": {
      opacity: 0,
      transform: "scale(0.9) translateY(20px)",
    },
    "100%": {
      opacity: 1,
      transform: "scale(1) translateY(0)",
    },
  },
});

/**
 * Efeito glassmorphism para Backgrounds/Overlays
 */
export const getGlassOverlay = (theme: Theme) => {
  const isDark = theme.palette.mode === "dark";
  return {
    backgroundColor: `rgba(${isDark ? "0, 0, 0" : "255, 255, 255"}, ${isDark ? 0.4 : 0.3})`,
    backdropFilter: "blur(20px) saturate(1.5)",
    WebkitBackdropFilter: "blur(20px) saturate(1.5)",
  };
};

/**
 * Efeito glassmorphism para Botões
 */
export const getGlassButton = (
  theme: Theme,
  variant: "primary" | "secondary" = "primary",
) => {
  const isDark = theme.palette.mode === "dark";
  const primaryColor = theme.palette.primary.main;
  const secondaryColor = theme.palette.secondary.main;

  const colorMap = {
    primary: primaryColor,
    secondary: secondaryColor,
  };

  const selectedColor = colorMap[variant];

  return {
    ...getGlassEffect(theme, "light"),
    background: `linear-gradient(135deg, ${selectedColor}20, ${selectedColor}10)`,
    color: theme.palette.getContrastText(selectedColor),
    border: `1px solid ${selectedColor}40`,
    "&:hover": {
      background: `linear-gradient(135deg, ${selectedColor}30, ${selectedColor}20)`,
      transform: "translateY(-1px)",
      boxShadow: `
                    0 6px 20px ${selectedColor}40,
                    inset 0 1px 0 rgba(255, 255, 255, ${isDark ? 0.2 : 0.3})
               `,
    },
    "&:active": {
      transform: "translateY(0)",
      boxShadow: `
                    0 2px 8px ${selectedColor}30,
                    inset 0 1px 0 rgba(255, 255, 255, ${isDark ? 0.1 : 0.2})
               `,
    },
  };
};

/**
 * Efeito glassmorphism para AppBar/Header
 */
export const getGlassAppBar = (theme: Theme) => ({
  ...getGlassEffect(theme, "medium"),
  borderTop: "none",
  borderLeft: "none",
  borderRight: "none",
  borderBottom: `1px solid rgba(255, 255, 255, ${theme.palette.mode === "dark" ? 0.1 : 0.2})`,
  boxShadow: `0 2px 16px rgba(0, 0, 0, ${theme.palette.mode === "dark" ? 0.3 : 0.1})`,
});

/**
 * Efeito glassmorphism para TextField/Inputs
 */
export const getGlassInput = (theme: Theme) => ({
  ...getGlassEffect(theme, "light"),
  // Sem padding extra - apenas o necessário para o glassmorphism
  "& .MuiOutlinedInput-root": {
    backgroundColor: "transparent",
    "& fieldset": {
      borderColor: `rgba(255, 255, 255, ${theme.palette.mode === "dark" ? 0.2 : 0.3})`,
    },
    "&:hover fieldset": {
      borderColor: `rgba(255, 255, 255, ${theme.palette.mode === "dark" ? 0.3 : 0.4})`,
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
    },
  },
});

/**
 * Utilitário para aplicar efeito glass em qualquer componente
 */
export const withGlassEffect =
  (theme: Theme, intensity: "light" | "medium" | "strong" = "medium") =>
  (sx: any) => ({
    ...getGlassEffect(theme, intensity),
    ...sx,
  });
